import React from "react";
import { colors } from "~/utils/colors";
import { ClientOnlyLazy } from "~/molecules/ClientOnlyLazy";
import type { DownloadPreviewImage } from "~/utils/downloadPreviewImages";

type Props = {
  images: DownloadPreviewImage[];
  alt?: string;
  embedded?: boolean;
};

const toAbsoluteImageSrc = (src: string) => (src.startsWith("//") ? `https:${src}` : src);

const SinglePreview: React.FC<{ image: DownloadPreviewImage; alt: string; embedded?: boolean }> = ({
  image,
  alt,
  embedded = false,
}) => (
  <>
    <style jsx>{`
      .previewOuter {
        background: ${colors.gray};
        border: 1px solid ${colors.gray3};
        border-radius: 8px;
        box-sizing: border-box;
        padding: 16px;
        width: 100%;
      }
      .previewViewport {
        background: ${colors.white};
        border: 1px solid ${colors.gray3};
        border-radius: 4px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        overflow: hidden;
      }
      .previewViewportEmbedded {
        border-radius: 8px;
        line-height: 0;
        overflow: hidden;
        width: 100%;
      }
      .previewEmbedded {
        box-sizing: border-box;
        width: 100%;
      }
      .previewImage {
        display: block;
        height: auto;
        width: 100%;
      }
    `}</style>
    {embedded ? (
      <div className="previewEmbedded">
        <div className="previewViewportEmbedded">
          <img className="previewImage" src={toAbsoluteImageSrc(image.src)} alt={alt} loading="lazy" />
        </div>
      </div>
    ) : (
      <div className="previewOuter">
        <div className="previewViewport">
          <img className="previewImage" src={toAbsoluteImageSrc(image.src)} alt={alt} loading="lazy" />
        </div>
      </div>
    )}
  </>
);

export const DocumentPreviewSlider: React.FC<Props> = ({ images, alt = "", embedded = false }) => {
  if (images.length === 0) {
    return null;
  }

  if (images.length === 1) {
    return <SinglePreview image={images[0]} alt={alt || "資料プレビュー"} embedded={embedded} />;
  }

  return (
    <ClientOnlyLazy
      load={() =>
        import("~/molecules/DocumentPreviewSwiper").then((module) => ({
          default: module.DocumentPreviewSwiper,
        }))
      }
      fallback={
        <SinglePreview
          image={images[0]}
          alt={alt ? `${alt} 1枚目` : "資料プレビュー 1枚目"}
          embedded={embedded}
        />
      }
      images={images}
      alt={alt}
      embedded={embedded}
    />
  );
};
